/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import React from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { type ButtonBase } from '../button';
import { CleanIconButton } from '../clean-icon-button';
import { FilledButton } from '../filled-button';
import {
  ExclamationCircleIconOutline,
  ExclamationTriangleIconOutline,
  InformationCircleIconOutline,
  XMarkIconOutline,
} from '../icons';
import { Modal, ModalCloseReason } from '../modal';
import { OutlinedButton } from '../outlined-button';
import { TextButton } from '../text-button';
import { Typography } from '../typography';
import { DialogContext, useDialogContext } from './dialog-context';
import {
  type DialogActionsProps,
  DialogCloseReason,
  type DialogContentProps,
  type DialogDescriptionProps,
  type DialogHeaderProps,
  type DialogImageProps,
  type DialogProps,
  type DialogSubtitleProps,
} from './dialog-types';

const convertCloseReason = (reason: ModalCloseReason): DialogCloseReason => {
  const dialogReason = reason as unknown as DialogCloseReason;
  if (Object.values(DialogCloseReason).includes(dialogReason)) {
    return dialogReason;
  }

  throw new Error(`Unknown reason '${reason}' received from popup`);
};
/* Stroke width on regular icons is 1.5px */
const typeIcons: Record<
  NonNullable<React.ComponentProps<typeof Dialog>['variant']>,
  React.ReactNode
> = {
  danger: <ExclamationCircleIconOutline htmlAttributes={{ strokeWidth: 3 }} />,
  info: <InformationCircleIconOutline htmlAttributes={{ strokeWidth: 3 }} />,
  warning: (
    <ExclamationTriangleIconOutline htmlAttributes={{ strokeWidth: 3 }} />
  ),
};

const DialogComponent = ({
  isOpen,
  onClose,
  children,
  variant,
  size,
  container,
  rootProps,
  hasDisabledCloseButton = false,
  htmlAttributes,
  className,
  style,
  ref,
  ...restProps
}: CommonProps<'div', DialogProps>) => {
  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose?.(e, DialogCloseReason.CLOSE_BUTTON_CLICK);
  };

  const handleClose = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    reason?: ModalCloseReason,
  ) => {
    // Dialog should not be closed when backdrop is clicked
    if (reason === ModalCloseReason.BACKDROP_CLICK) {
      return;
    }

    onClose?.(e, reason ? convertCloseReason(reason) : undefined);
  };

  if (!isOpen) {
    return null;
  }

  const classes = classNames('ndl-dialog', className, {
    'ndl-with-close-button': !hasDisabledCloseButton,
    'ndl-with-icon': !!variant,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      container={container}
      rootProps={rootProps}
      size={size}
      htmlAttributes={htmlAttributes}
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
    >
      <DialogContext.Provider value={{ variant }}>
        {!hasDisabledCloseButton && (
          <CleanIconButton
            size="medium"
            className="ndl-dialog-close"
            onClick={handleCloseClick}
            description="Close"
            htmlAttributes={{
              'data-testid': 'ndl-dialog-close',
              role: 'button',
            }}
          >
            <XMarkIconOutline />
          </CleanIconButton>
        )}
        <div className="ndl-dialog-content-wrapper">
          {variant && (
            <div
              className={`ndl-dialog-type-icon ndl-${variant}`}
              data-testid="ndl-dialog-type-icon"
            >
              {typeIcons[variant]}
            </div>
          )}
          <div className="n-flex n-flex-col n-flex-1 n-w-full">{children}</div>
        </div>
      </DialogContext.Provider>
    </Modal>
  );
};

const Actions = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, DialogActionsProps>) => {
  const { variant } = useDialogContext();
  const classes = classNames('ndl-dialog-actions', className);
  const Component: React.ElementType = as ?? 'div';

  let childrenToRender = children;

  // For danger dialogs, we want to convert primary buttons to danger buttons by default
  if (variant === 'danger') {
    childrenToRender = React.Children.map(children, (element) => {
      if (!React.isValidElement(element)) {
        return;
      }
      if (
        element.type === OutlinedButton ||
        element.type === FilledButton ||
        element.type === TextButton
      ) {
        const buttonElement = element as React.ReactElement<
          React.ComponentProps<typeof ButtonBase>
        >;

        if (
          buttonElement.props.variant === 'primary' ||
          buttonElement.props.variant === undefined
        ) {
          return React.cloneElement(buttonElement, {
            ...buttonElement.props,
            variant: 'danger',
          });
        }
      }
      return element;
    });
  }

  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {childrenToRender}
    </Component>
  );
};

const Header = <T extends React.ElementType = 'span'>({
  as,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, DialogHeaderProps>) => {
  const classes = classNames('ndl-dialog-header', className);
  const Component: React.ElementType = as ?? 'span';

  return (
    <Typography
      as={Component}
      variant="title-2"
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Typography>
  );
};

const Subtitle = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, DialogSubtitleProps>) => {
  const classes = classNames('ndl-dialog-subtitle', className);
  const Component: React.ElementType = as ?? 'div';

  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const Description = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, DialogDescriptionProps>) => {
  const classes = classNames('ndl-dialog-description', className);
  const Component: React.ElementType = as ?? 'div';
  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const Content = <T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, DialogContentProps>) => {
  const classes = classNames('ndl-dialog-content', className);
  const Component: React.ElementType = as ?? 'div';

  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};

const Image = ({
  src,
  alt,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'img', DialogImageProps>) => {
  const classes = classNames('ndl-dialog-image', className);

  return (
    <img
      src={src}
      alt={alt}
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    />
  );
};

const Dialog = Object.assign(DialogComponent, {
  Actions,
  Content,
  Description,
  Header,
  Image,
  Subtitle,
});

export { Dialog };
